import { Component, Input, ViewChild, inject, ElementRef, EventEmitter, Output, OnDestroy, AfterViewInit, ChangeDetectorRef, input, OnInit } from '@angular/core'
import { CommonModule, TitleCasePipe } from '@angular/common'
import { toObservable } from '@angular/core/rxjs-interop'
import { SeFeSidePanelModule } from 'se-fe-side-panel'
import { SeFeHeaderModule } from 'se-fe-header'

import { SeFeAlertModule } from 'se-fe-alert'
import { SeFeAvatarModule } from 'se-fe-avatar'
import { SeFeLinkModule } from 'se-fe-link'
import { SeFeCardModule } from 'se-fe-card'
import { SeFeTabGroupModule } from 'se-fe-tabs'
import { SeFeLabelValuePairModule } from 'se-fe-label-value-pair'
import { SeFeDateTimeModule } from 'se-fe-date-time'
import { SeFeGridModule } from 'se-fe-grid'
import { ProfileSidePanelChange, ProfileSidePanelConfig } from './profile-side-panel.types'
import { SeFeTranslationsLoader, SeFeTranslationsModule } from 'se-fe-translations'
import { TranslateService } from '@ngx-translate/core'
import { createTranslationFn, LazyLoadComponentConfig, ProfileDetailRequest } from '@se-po/shared-util'
import { seFeIconBadgeVerifyFill, seFeIconChevronLeft, SeFeIconModule, seFeIconMore, seFeIconPerson, SeFeIconRegistry, seFeIconRemove, seFeIconSend, seFeIconWarning } from 'se-fe-icon'
import { SeFeButtonModule } from 'se-fe-button'
import { SeFeMenuModule, SeFeMenuOptions } from 'se-fe-menu'
import { SeFeChipModule } from 'se-fe-chip'
import { catchError, combineLatest, distinctUntilChanged, EMPTY, filter, mergeMap, Observable, of, ReplaySubject, shareReplay, startWith, Subject, switchMap, take, takeUntil, map, tap, withLatestFrom, zip } from 'rxjs'
import { SeFeSkeletonModule } from 'se-fe-skeleton'
import { Store } from '@ngrx/store'
import { NginAdminManagementApiActions, NginAdminManagementSelectors, AdminManagement } from 'se-fe-data-access/ngin-admin-management'
import { NginGroupApiActions } from 'se-fe-data-access/ngin-group'
import { NginSurveyResultApiActions } from 'se-fe-data-access/ngin-survey-result'
import { NginUserPersonaApiActions, NginUserPersonaSelectors, UserPersona } from 'se-fe-data-access/ngin-user-persona'
import { LazyLoadGroupComponent } from './lazy-load-group.component'
import { MODULES, ProfileSidePanelModule } from './profile-side-panel-modules'
import { ProfileSidePanelModel, selectProfileSidePanelModel } from './profile-side-panel.model'
import { ProfileSidePanelHeaderSkeletonComponent } from './profile-side-panel-header-skeleton.component'
import { LazyLoadComponent, LazyLoadComponentOutputs } from './lazy-load.component'
import { PipesModule } from '@se-po/shared-data-access-pipes'
import { ProfileAdminService, type AdminInvite, type PersonaInviteResult } from '@se-po/shared-feature-profile-admin-actions'
import { AffiliationService, OrganizationService } from '@se-po/shared-data-access-services'
import { BossServiceOrganizationApiActions, Organization } from 'se-fe-data-access/boss-service-organization'
import { CentralServiceResources as cs } from 'se-resource-types'
import {
  PROFILE_SIDE_PANEL_ADMIN_DETAIL_REQUEST_KEY,
  PROFILE_SIDE_PANEL_ADMIN_UPDATE_REQUEST_KEY,
  PROFILE_SIDE_PANEL_ORGANIZATION_DETAIL_REQUEST_KEY,
  PROFILE_SIDE_PANEL_PERSONA_DETAIL_REQUEST_KEY,
  PROFILE_SIDE_PANEL_PERSONA_UPDATE_REQUEST_KEY,
  PROFILE_SIDE_PANEL_REQUEST_PREFIX,
  PROFILE_SIDE_PANEL_VERIFIED_INFO_DETAIL_REQUEST_KEY,
} from './profile-side-panel-request-keys'
import { Persona, UserServicePersonaApiActions, UserServicePersonaSelectors } from 'se-fe-data-access/user-service-persona'
import { VerifiedInformation } from 'se-resource-types/dist/lib/UserService'
import { SeFeAccordionModule } from 'se-fe-accordion'
import { SeFeTooltipModule } from 'se-fe-tooltip'
import {
  PROFILE_SIDE_PANEL_DETAILS,
  isProfileSidePanelDetailType,
  ProfileSidePanelDetail,
} from './profile-side-panel-details'
import { DENIED_PROFILE_ACCESS, ProfileAccess, ProfileAccessService } from '@se-po/shared-data-access-profile'
import { profileModules } from './profile-module-policy'

const MEMBERSHIP_PURCHASE_TOGGLE = 'verify_info_on_membership_purchase'

const ICONS = [
  seFeIconMore,
  seFeIconChevronLeft,
  seFeIconSend,
  seFeIconRemove,
  seFeIconPerson,
  seFeIconBadgeVerifyFill,
  seFeIconWarning
]

type ProfileSidePanelOutputs = {
  closeDetail: void
  detailRequest: ProfileDetailRequest<string, SeFeMenuOptions>
  permissionChange: any
}

interface ProfileSidePanelDetailHistoryEntry {
  detail: ProfileSidePanelDetail
  scrollTop: number
}

type ProfileAccessStatus = 'loading' | 'allowed' | 'denied' | 'error'

interface ProfileAccessState {
  access: ProfileAccess
  config: ProfileSidePanelConfig
  status: ProfileAccessStatus
}

@Component({
  selector: 'se-po-feature-profile-side-panel',
  standalone: true,
  imports: [
    CommonModule,
    SeFeAccordionModule,
    SeFeAlertModule,
    SeFeAvatarModule,
    SeFeButtonModule,
    SeFeIconModule,
    SeFeChipModule,
    SeFeCardModule,
    SeFeDateTimeModule,
    SeFeSidePanelModule,
    SeFeGridModule,
    SeFeHeaderModule,
    SeFeMenuModule,
    SeFeLabelValuePairModule,
    SeFeLinkModule,
    SeFeTabGroupModule,
    SeFeTooltipModule,
    SeFeTranslationsModule,
    SeFeSkeletonModule,
    ProfileSidePanelHeaderSkeletonComponent,
    LazyLoadComponent,
    LazyLoadGroupComponent,
    TitleCasePipe,
    PipesModule,
],

  templateUrl: './profile-side-panel.component.html',
  styleUrls: ['./profile-side-panel.component.scss'],
})
export class ProfileSidePanelComponent implements OnDestroy, AfterViewInit, OnInit {

  @ViewChild('sidePanel', { read: ElementRef }) private sidePanel?: ElementRef<HTMLElement>

  @Output() public closePanel = new EventEmitter<void>()
  @Output() public sidePanelChange = new EventEmitter<ProfileSidePanelChange>()
  @Output() public permissionChange = new EventEmitter<any>()

  public config = input<ProfileSidePanelConfig | null>(null)

  @Input() public set showPanel(value: boolean) {
    if (!value) this.closeDetail()
    this._showPanel = value
  }

  public get showPanel(): boolean {
    return this._showPanel && this.translationsLoaded
  }

  public translateKey = 'SE_PO_SHARED_FEATURE_PROFILE_SIDE_PANEL.'
  public t = createTranslationFn(inject(TranslateService), this.translateKey)
  public icons = inject(SeFeIconRegistry).registerIcons(ICONS)

  public showMismatchAlert = true
  public translationsLoaded = false
  public headerLoaded = false

  public menuOptions: SeFeMenuOptions = {}
  public pendingMenuOptions: SeFeMenuOptions = {}

  public get activeDetail(): ProfileSidePanelDetail | undefined {
    return this.detailHistory.at(-1)?.detail
  }

  public get activeDetailComponentConfig(): LazyLoadComponentConfig | null {
    return this.activeDetail ? PROFILE_SIDE_PANEL_DETAILS[this.activeDetail.type] : null
  }

  public get sidePanelMenuOptions(): SeFeMenuOptions | undefined {
    if (this.profileAccessStatus !== 'allowed') return undefined
    if (this.activeDetail) return this.activeDetail.menuOptions
    if (this.currentVm?.pendingProfile) return undefined
    return this.menuOptions
  }

  public profileAccessStatus: ProfileAccessStatus = 'loading'
  private _showPanel = false
  private detailHistory: ProfileSidePanelDetailHistoryEntry[] = []
  private overviewScrollTop = 0
  private scrollRestoreId = 0

  private componentDestroyed$ = new Subject<void>()
  private currentVm?: ProfileSidePanelModel
  private cdr = inject(ChangeDetectorRef)
  private el = inject(ElementRef)
  private store = inject(Store)
  private translationsLoader = inject(SeFeTranslationsLoader)
  private profileAdminService = inject(ProfileAdminService)
  private affiliationService = inject(AffiliationService)
  private organizationService = inject(OrganizationService)
  private profileAccessService = inject(ProfileAccessService)
  private lastVerifiedInfoLoadKey?: string
  private requestOrgId: string | number | null | undefined
  private requestPersonaId: string | number | null | undefined
  private orgIsClubCache = new Map<string, Observable<boolean>>()
  private verifiedInfoFeatureToggleCache = new Map<string, Observable<boolean>>()
  private authorizedConfig$ = new ReplaySubject<ProfileSidePanelConfig>(1)
  private currentAccess: ProfileAccess = DENIED_PROFILE_ACCESS

  // TODO: can we get rid of this if we use signal store?
  private config$ = toObservable(this.config).pipe(
    filter((config): config is ProfileSidePanelConfig => !!config),
    distinctUntilChanged((previous, current) =>
      this.resolvedOrgId(previous) === this.resolvedOrgId(current) &&
      this.rootOrganizationId(previous.organization) === this.rootOrganizationId(current.organization) &&
      previous.persona?.id === current.persona?.id &&
      previous.currentUser === current.currentUser &&
      this.sameModules(previous.modules, current.modules) &&
      this.samePolicy(previous.policy, current.policy)
    )
  )

  public vm$: Observable<ProfileSidePanelModel> = this.authorizedConfig$.pipe(
    switchMap(config =>
      this.hasVerifiedInfoFeatureToggle(config).pipe(
        switchMap(hasFeatureToggle =>
          this.currentOrganizationIsClub(config, hasFeatureToggle).pipe(
            map(isClub => ({ hasFeatureToggle, isClub }))
          )
        ),
        tap(({ hasFeatureToggle }) => this.loadVerifiedInfo(config, hasFeatureToggle)),
        switchMap(({ hasFeatureToggle, isClub }) =>
          this.store.select(selectProfileSidePanelModel({
            ...config,
            orgId: this.resolvedOrgId(config),
            personaId: config.persona?.id,
            currentUser: config.currentUser,
            hasVerifiedInfoFeatureToggle: hasFeatureToggle,
            isClub,
          }))
        )
      )
    ),
    tap(vm => {
      this.currentVm = vm
      this.buildMenuOptions(vm)
      const config = this.config()
      if (config) {
        this.currentModules = profileModules({
          access: this.currentAccess,
          config,
          isAdmin: vm.isAdmin,
          hasOwner: vm.hasOwner,
        })
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  private blockedConfig$ = toObservable(this.config).pipe(
    map(config => ({ personaId: config?.persona?.id, blocked: config?.blocked === true })),
    distinctUntilChanged((previous, current) =>
      previous.personaId === current.personaId && previous.blocked === current.blocked
    )
  )

  public profileFeatureInputs$: Observable<Record<string, unknown>> = combineLatest([
    this.authorizedConfig$,
    this.blockedConfig$,
  ]).pipe(
    map(([config, blockedConfig]) => ({
      orgId: this.resolvedOrgId(config),
      personaId: config.persona?.id,
      organization: config?.organization,
      currentUser: config?.currentUser,
      policy: config?.policy,
      blocked: blockedConfig.personaId === config.persona?.id ? blockedConfig.blocked : true,
    })),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  public currentModules: ProfileSidePanelModule[] = []
  public moduleConfigs = MODULES
  public profileComponentOutputs: LazyLoadComponentOutputs<ProfileSidePanelOutputs> = {
    closeDetail: () => this.closeDetail(),
    detailRequest: event => this.handleDetailRequest(event),
    permissionChange: event => this.handlePermissionChange(event),
  }
  public detailComponentOutputs: LazyLoadComponentOutputs = {
    closeDetail: () => this.closeDetail(),
    menuOptionsChange: menuOptions => this.updateDetailMenuOptions(menuOptions as SeFeMenuOptions),
  }

  constructor() {
    this.config$.pipe(
      switchMap(config => this.loadProfileAccess(config)),
      takeUntil(this.componentDestroyed$)
    ).subscribe(state => this.applyProfileAccess(state))
  }

  public ngOnInit(): void {
    this.initInviteStreams()
  }

  public ngAfterViewInit(): void {
    this.listenForEditPersonaResult()
    this.listenForEditVerifiedInfoResult()
    void this.loadTranslations()
  }

  public ngOnDestroy(): void {
    this.clearRequests()
    this.componentDestroyed$.next()
    this.componentDestroyed$.complete()
  }

  private async loadTranslations(): Promise<void> {
    await this.translationsLoader.load('assets/shared/feature-profile-side-panel/src/assets/translations')
    this.translationsLoaded = true
    this.buildMenuOptions(this.currentVm)
    this.cdr.detectChanges()
  }

  private initInviteStreams(): void {
    this.profileAdminService.personaInviteResult$.pipe(
      mergeMap(result =>
        this.store.select(NginUserPersonaSelectors.selectNginUserPersonaById(result.personaId)).pipe(
          take(1),
          filter((persona): persona is UserPersona => !!persona),
          tap(persona => this.updateNginUserPersonaInvite(persona, result))
        )
      ),
      takeUntil(this.componentDestroyed$)
    ).subscribe()

    this.profileAdminService.personaInviteCancelResult$.pipe(
      mergeMap(personaId =>
        this.store.select(NginUserPersonaSelectors.selectNginUserPersonaById(personaId)).pipe(
          take(1),
          filter((persona): persona is UserPersona => !!persona),
          tap(persona => this.clearNginUserPersonaInvite(persona))
        )
      ),
      takeUntil(this.componentDestroyed$)
    ).subscribe()

    this.profileAdminService.adminInviteCancelResult$.pipe(
      mergeMap(inviteId => {
        const personaId = this.config()?.persona?.id
        if (!personaId) return EMPTY

        return this.store.select(NginAdminManagementSelectors.selectNginAdminManagementByPersonaId(personaId)).pipe(
          take(1),
          filter((admin): admin is AdminManagement => admin?.admin_invite_id?.toString() === inviteId),
          tap(admin => this.clearNginAdminManagementInvite(admin))
        )
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe()
  }

  public buildMenuOptions(vm?: ProfileSidePanelModel, triggerEl?: HTMLElement): void {
    this.menuOptions = {
      sections: [
        {
          menuItems: [
            { text: this.t('MENU.view'), icon: 'person', action: () => this.profileRedirect() }
          ]
        }
      ]
    }

    if (!vm?.pendingProfile) {
      this.pendingMenuOptions = {}
      return
    }

    const menuItems = vm.isAdmin || vm.inviteStatus === 'invited'
      ? [
        {
          text: this.t('MENU.resend_invite'),
          icon: 'send',
          action: () => vm.isAdmin ? this.openAdminInviteModal(vm, triggerEl) : this.openPersonaInviteModal(vm, 'resend', triggerEl),
        },
        {
          text: this.t('MENU.cancel_invite'),
          icon: 'remove',
          action: () => vm.isAdmin ? this.cancelAdminInvite(vm) : this.cancelPersonaInvite(vm),
        },
      ]
      : [
        { text: this.t('MENU.send_invite'), icon: 'send', action: () => this.openPersonaInviteModal(vm, 'send', triggerEl) },
      ]

    this.pendingMenuOptions = {
      ...this.pendingMenuOptions,
      sections: [
        {
          menuItems
        }
      ]
    }
  }

  public openPersonaInviteModal(vm: ProfileSidePanelModel, mode: 'send' | 'resend', triggerEl?: HTMLElement): void {
    const personId = vm.personaId?.toString()
    const orgId = vm.orgId?.toString()
    if (!personId || !orgId) return

    this.profileAdminService.personaInvite({
      personId,
      personName: vm.title || 'this person',
      email: vm.email === '--' ? '' : vm.email,
      emailId: vm.emailId,
      orgId,
      triggerEl: triggerEl || document.activeElement as HTMLElement,
      mode,
    })
  }

  private updateNginUserPersonaInvite(persona: UserPersona, result: PersonaInviteResult): void {
    if (!result.email) return

    this.store.dispatch(NginUserPersonaApiActions.loadNginUserPersonaSuccess({
      resource: {
        ...persona,
        orphan_invite_id: result.orphanInviteId ?? persona.orphan_invite_id,
        email_addresses: this.withUpdatedPrimaryEmail(persona, result.email),
      },
      requestKey: PROFILE_SIDE_PANEL_PERSONA_UPDATE_REQUEST_KEY,
    }))
  }

  private withUpdatedPrimaryEmail(persona: UserPersona, email: string): NonNullable<UserPersona['email_addresses']> {
    const emailAddresses = persona.email_addresses || []
    const hasPrimaryEmail = emailAddresses.some(emailAddress => emailAddress.is_primary)

    if (hasPrimaryEmail) {
      return emailAddresses.map(emailAddress =>
        emailAddress.is_primary ? { ...emailAddress, address: email } : emailAddress
      )
    }

    return [
      {
        address: email,
        is_primary: true,
      } as NonNullable<UserPersona['email_addresses']>[number]
    ]
  }

  private clearNginUserPersonaInvite(persona: UserPersona): void {
    this.store.dispatch(NginUserPersonaApiActions.loadNginUserPersonaSuccess({
      resource: {
        ...persona,
        orphan_invite_id: undefined,
      },
      requestKey: PROFILE_SIDE_PANEL_PERSONA_UPDATE_REQUEST_KEY,
    }))
  }

  private clearNginAdminManagementInvite(admin: AdminManagement): void {
    this.store.dispatch(NginAdminManagementApiActions.loadNginAdminManagementSuccess({
      resource: {
        ...admin,
        admin_invite_id: undefined,
      } as unknown as AdminManagement,
      requestKey: PROFILE_SIDE_PANEL_ADMIN_UPDATE_REQUEST_KEY,
    }))
  }

  public openAdminInviteModal(vm: ProfileSidePanelModel, trigger?: HTMLElement): void {
    const admin = vm.admin
    const orgId = vm.orgId?.toString()
    if (!admin || !orgId) return

    this.profileAdminService.adminInvite({
      orgId,
      inviteId: admin.admin_invite_id?.toString(),
      firstName: admin.first_name || '',
      lastName: admin.last_name || '',
      name: vm.title,
      email: admin.email_address || '',
      positions: admin.positions || [],
      permissionType: admin.permission_type as AdminInvite['permissionType'],
      hq_tools_access: !!admin.hq_tools_access,
      website_access: !!admin.website_access,
      financials_access: !!admin.financials_access,
      super_reg_access: !!admin.super_reg_access,
      compliance_access: !!admin.compliance_access,
      governing_season_admin_access: !!admin.governing_season_admin_access,
      governing_season_view_access: !!admin.governing_season_view_access,
      trigger: trigger || document.activeElement as HTMLElement,
    })
  }

  public cancelPersonaInvite(vm: ProfileSidePanelModel): void {
    const personId = vm.personaId?.toString()
    if (!personId) return

    this.profileAdminService.cancelPersonaInvite({
      personId,
      successMessage: this.t('TOASTS.cancel_invite_success'),
      errorMessage: this.t('TOASTS.cancel_invite_error'),
    }).pipe(
      catchError(() => EMPTY),
      takeUntil(this.componentDestroyed$)
    ).subscribe()
  }

  public cancelAdminInvite(vm: ProfileSidePanelModel): void {
    const orgId = vm.orgId?.toString()
    const inviteId = vm.admin?.admin_invite_id?.toString()
    if (!orgId || !inviteId) return

    this.profileAdminService.cancelAdminInvite({
      orgId,
      inviteId,
      successMessage: this.t('TOASTS.cancel_admin_invite_success'),
      errorMessage: this.t('TOASTS.cancel_admin_invite_error'),
    }).pipe(
      catchError(() => EMPTY),
      takeUntil(this.componentDestroyed$)
    ).subscribe(() => {
      this.close()
      this.permissionChange.emit({ success: true })
    })
  }

  private initializeConfig(config: ProfileSidePanelConfig): void {
    const { admin, organization, persona } = config
    const resolvedOrgId = this.resolvedOrgId(config)
    const resolvedPersonaId = persona?.id ?? ''
    const personaChanged = this.requestPersonaId !== undefined && this.requestPersonaId !== resolvedPersonaId
    const organizationChanged = this.requestOrgId !== undefined && this.requestOrgId !== resolvedOrgId

    if (personaChanged || organizationChanged) {
      this.currentVm = undefined
      this.closeDetail(false)
      this.overviewScrollTop = 0
      this.restorePanelScroll(0)
    }

    if (personaChanged || organizationChanged) {
      this.clearPersonaRequests()
    }
    if (organizationChanged) {
      this.clearOrganizationRequests()
    }
    this.requestPersonaId = resolvedPersonaId
    this.requestOrgId = resolvedOrgId

    // TODO: Can/should we dry this up with a helper?

    if (this.hasRootOrganizations(organization)) {
      this.store.dispatch(BossServiceOrganizationApiActions.loadBossServiceOrganizationSuccess({
        resource: organization as Organization,
        requestKey: PROFILE_SIDE_PANEL_ORGANIZATION_DETAIL_REQUEST_KEY,
      }))
    } else if (resolvedOrgId) {
      this.store.dispatch(BossServiceOrganizationApiActions.loadBossServiceOrganization({
        resourceId: resolvedOrgId,
        params: { include: 'root_organizations' },
        requestKey: PROFILE_SIDE_PANEL_ORGANIZATION_DETAIL_REQUEST_KEY,
      }))
    }

    if (admin?.persona_id) {
      this.store.dispatch(NginAdminManagementApiActions.loadNginAdminManagementSuccess({
        resource: admin as AdminManagement,
        requestKey: PROFILE_SIDE_PANEL_ADMIN_DETAIL_REQUEST_KEY,
      }))
    } else {
      this.store.dispatch(NginAdminManagementApiActions.loadNginAdminManagement({
        resourceId: resolvedPersonaId,
        params: { org_id: resolvedOrgId },
        requestKey: PROFILE_SIDE_PANEL_ADMIN_DETAIL_REQUEST_KEY,
      }))
    }

    if (persona?.first_name && persona?.persona_type) {
      this.store.dispatch(NginUserPersonaApiActions.loadNginUserPersonaSuccess({
        resource: persona as UserPersona,
        requestKey: PROFILE_SIDE_PANEL_PERSONA_DETAIL_REQUEST_KEY,
      }))
    } else {
      this.store.dispatch(NginUserPersonaApiActions.loadNginUserPersona({
        resourceId: resolvedPersonaId,
        params: { org_id: resolvedOrgId },
        requestKey: PROFILE_SIDE_PANEL_PERSONA_DETAIL_REQUEST_KEY,
      }))
    }
  }

  private loadProfileAccess(config: ProfileSidePanelConfig): Observable<ProfileAccessState> {
    const orgId = this.resolvedOrgId(config)
    if (orgId === undefined || orgId === null || orgId === '') {
      return of({ access: DENIED_PROFILE_ACCESS, config, status: 'error' })
    }

    return this.profileAccessService.resolve(orgId, config.organization).pipe(
      map(access => ({
        access,
        config,
        status: access.profiles ? 'allowed' as const : 'denied' as const,
      })),
      catchError(() => of({ access: DENIED_PROFILE_ACCESS, config, status: 'error' as const })),
      startWith({ access: DENIED_PROFILE_ACCESS, config, status: 'loading' as const })
    )
  }

  private applyProfileAccess(state: ProfileAccessState): void {
    this.profileAccessStatus = state.status
    this.currentModules = []

    if (state.status === 'loading') this.closeDetail(false)

    if (state.status === 'allowed') {
      this.currentAccess = state.access
      this.currentModules = profileModules({
        access: state.access,
        config: state.config,
        isAdmin: false,
        hasOwner: !!state.config.persona?.owner,
      })
      this.initializeConfig(state.config)
      this.authorizedConfig$.next(state.config)
    } else if (state.status !== 'loading') {
      this.currentAccess = DENIED_PROFILE_ACCESS
      this.closeDetail(false)
      this.clearRequests()
    }

    this.cdr.markForCheck()
  }

  private resolvedOrgId(config: ProfileSidePanelConfig): string | number | null | undefined {
    return config.organization?.id ?? config.orgId
  }

  private hasRootOrganizations(organization: Partial<Organization> | undefined): boolean {
    return Boolean(organization && 'root_organizations' in organization)
  }

  private rootOrganizationId(organization: Partial<Organization> | undefined): string | number | undefined {
    return organization?.root_organizations?.[0]?.id
  }

  private sameModules(
    previous: ProfileSidePanelModule[] | undefined,
    current: ProfileSidePanelModule[] | undefined
  ): boolean {
    return previous === current || (
      previous?.length === current?.length &&
      previous?.every((module, index) => module === current?.[index]) === true
    )
  }

  private samePolicy(
    previous: ProfileSidePanelConfig['policy'],
    current: ProfileSidePanelConfig['policy']
  ): boolean {
    return previous === current || (
      previous?.access_grant_permissions === current?.access_grant_permissions &&
      previous?.access_verify_product === current?.access_verify_product
    )
  }

  private loadVerifiedInfo(config: ProfileSidePanelConfig, hasFeatureToggle: boolean): void {
    const orgId = this.resolvedOrgId(config)?.toString()
    const personaId = config.persona?.id?.toString()

    if (!orgId || !personaId || !hasFeatureToggle) return

    const loadKey = `${personaId}:${orgId}`
    if (loadKey === this.lastVerifiedInfoLoadKey) return

    this.lastVerifiedInfoLoadKey = loadKey
    this.store.dispatch(UserServicePersonaApiActions.loadUserServicePersona({
      resourceId: personaId,
      params: { org_id: orgId },
      requestKey: PROFILE_SIDE_PANEL_VERIFIED_INFO_DETAIL_REQUEST_KEY,
    }))
  }

  private hasVerifiedInfoFeatureToggle(config: ProfileSidePanelConfig): Observable<boolean> {
    const orgId = this.resolvedOrgId(config)?.toString()
    if (!orgId) return of(false)
    const cachedFeatureToggle = this.verifiedInfoFeatureToggleCache.get(orgId)
    if (cachedFeatureToggle) return cachedFeatureToggle

    const organization$: Observable<any> = this.hasRootOrganizations(config.organization)
      ? of(config.organization)
      : this.organizationService.findPublic(orgId, { include: 'root_organizations' })

    const rootOrgHasFeature$ = organization$.pipe(
      map(organization => organization?.root_organizations?.[0] || null),
      switchMap(rootOrg => {
        if (!rootOrg) return of(false)

        return this.organizationService.hasFeatureToggleEnabled(rootOrg.id.toString(), MEMBERSHIP_PURCHASE_TOGGLE).pipe(
          catchError(() => of(false))
        )
      }),
      catchError(() => of(false))
    )
    const orgHasFeature$ = this.organizationService.hasFeatureToggleEnabled(orgId, MEMBERSHIP_PURCHASE_TOGGLE).pipe(
      catchError(() => of(false))
    )

    const hasFeatureToggle$ = zip(rootOrgHasFeature$, orgHasFeature$).pipe(
      map(([rootOrgHasFeature, orgHasFeature]) => rootOrgHasFeature || orgHasFeature),
      shareReplay({ bufferSize: 1, refCount: false })
    )
    this.verifiedInfoFeatureToggleCache.set(orgId, hasFeatureToggle$)

    return hasFeatureToggle$
  }

  private currentOrganizationIsClub(config: ProfileSidePanelConfig, hasFeatureToggle: boolean): Observable<boolean> {
    const orgId = this.resolvedOrgId(config)?.toString()
    if (!orgId || !hasFeatureToggle) return of(false)
    const cachedIsClub = this.orgIsClubCache.get(orgId)
    if (cachedIsClub) return cachedIsClub

    const isClub$ = this.affiliationService.findAll(orgId).pipe(
      map(({ result: affiliations }) => affiliations.length === 0),
      catchError(() => of(false)),
      shareReplay({ bufferSize: 1, refCount: false })
    )
    this.orgIsClubCache.set(orgId, isClub$)

    return isClub$
  }

  public profileRedirect(): void {
    const customEvent = new CustomEvent('directoryPostMessage', {
      detail: {
        navigation: true,
        state: 'persona.info.profile',
        attrs: { personaId: this.config()?.persona?.id }
      },
      bubbles: true,
      composed: true
    })
    this.el.nativeElement?.dispatchEvent(customEvent)
  }

  public openEditPersona(vm: ProfileSidePanelModel, event?: MouseEvent): void {
    if (!vm.editable) return

    const trigger = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null

    if (vm.verifiedInfo) {
      if (!vm.orgId) return

      this.profileAdminService.editVerifiedInfo({
        verifiedInfo: vm.verifiedInfo,
        ...(vm.persona ? { memberInfo: vm.persona } : {}),
        orgId: vm.orgId,
        trigger,
      })
      return
    } else {
      this.profileAdminService.editPersona({
        profile: this.profileForEditPersonaModal(vm),
        requiredFields: ['gender'],
        orgId: vm.orgId,
        trigger,
      })
    }
  }

  public close(): void {
    this.closeDetail()
    this.showPanel = false
    this.closePanel.emit()
  }

  public afterClose(): void {
    this.clearRequests()
  }

  public handlePermissionChange(event: any): void {
    if (event?.event === 'adminRemoved') this.close()

    this.permissionChange.emit(event)
  }

  public handleDetailRequest(request: ProfileDetailRequest<string, SeFeMenuOptions>): void {
    if (!isProfileSidePanelDetailType(request.type)) return

    const { navigation, ...requestDetail } = request
    const detail: ProfileSidePanelDetail = { ...requestDetail, type: request.type }

    if (navigation === 'replace') {
      this.replaceDetail(detail)
      return
    }

    const currentScrollTop = this.panelScrollTop()
    const current = this.detailHistory.at(-1)
    if (current) current.scrollTop = currentScrollTop
    else this.overviewScrollTop = currentScrollTop

    this.detailHistory.push({ detail, scrollTop: 0 })
    this.restorePanelScroll(0)
  }

  private replaceDetail(detail: ProfileSidePanelDetail): boolean {
    const kind = this.detailKind(detail)
    for (let index = this.detailHistory.length - 1; index >= 0; index--) {
      const replacement = this.detailHistory[index]
      const historyDetail = replacement.detail
      const matches = historyDetail.type === detail.type &&
        (kind === undefined || this.detailKind(historyDetail) === kind)
      if (!matches) continue

      replacement.detail = detail
      return true
    }

    return false
  }

  private detailKind(detail: ProfileSidePanelDetail): unknown {
    // Feature-owned detail routers use this input to select the view they render.
    const featureDetail = detail.componentInputs['detail']
    if (!featureDetail || typeof featureDetail !== 'object' || !('kind' in featureDetail)) return undefined

    return featureDetail.kind
  }

  public updateDetailMenuOptions(menuOptions: SeFeMenuOptions): void {
    const current = this.detailHistory.at(-1)
    if (!current) return

    current.detail = { ...current.detail, menuOptions }
  }

  public navigateBack(): void {
    const current = this.detailHistory.pop()
    if (!current) return

    current.detail.onClose?.()
    this.restorePanelScroll(this.detailHistory.at(-1)?.scrollTop ?? this.overviewScrollTop)
  }

  public closeDetail(restoreOverview = true): void {
    const history = this.detailHistory
    if (!history.length) return

    this.detailHistory = []
    history.reverse().forEach(({ detail }) => detail.onClose?.())

    if (restoreOverview) this.restorePanelScroll(this.overviewScrollTop)
  }

  private panelScrollTop(): number {
    return this.panelScrollElement()?.scrollTop ?? 0
  }

  private restorePanelScroll(scrollTop: number): void {
    const restoreId = ++this.scrollRestoreId
    requestAnimationFrame(() => {
      if (restoreId !== this.scrollRestoreId) return

      const scrollElement = this.panelScrollElement()
      if (scrollElement) scrollElement.scrollTop = scrollTop
    })
  }

  private panelScrollElement(): HTMLElement | null {
    return this.sidePanel?.nativeElement.querySelector<HTMLElement>('.se-fe-side-panel__wrapper') ?? null
  }

  private isAdminRemovedEvent(event: any): boolean {
    return event?.event === 'adminRemoved' || event?.params?.permission_type === null
  }

  private profileForEditPersonaModal(vm: ProfileSidePanelModel): cs.Households.UserProfile {
    const persona = vm.persona || {} as UserPersona

    return {
      id: vm.personaId || persona.id,
      access: 'manager',
      relationship: 'other',
      persona: {
        id: Number(vm.personaId || persona.id),
        first_name: persona.first_name,
        middle_name: persona.middle_name,
        last_name: persona.last_name,
        nickname: persona.nickname,
        suffix: persona.suffix,
        date_of_birth: persona.date_of_birth,
        graduation_year: persona.graduation_year,
        gender: persona.gender,
        email_addresses: persona.email_addresses,
        addresses: persona.addresses,
        phone_numbers: persona.phone_numbers,
        profile_images: persona.profile_images,
      } as cs.Profiles.Persona
    }
  }

  private listenForEditPersonaResult(): void {
    this.profileAdminService.editPersonaResult$
      .pipe(
        withLatestFrom(this.vm$),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(([updatedPersona, vm]) => {
        const existingPersona = vm.persona
        if (String(existingPersona?.id) !== String(updatedPersona.id)) return

        this.store.dispatch(NginUserPersonaApiActions.setNginUserPersonaEntity({
          resource: this.withEditedPersonaFields(existingPersona, updatedPersona),
        }))
        this.sidePanelChange.emit({ type: 'editPersonaChange', data: updatedPersona })
      })
  }

  private listenForEditVerifiedInfoResult(): void {
    this.profileAdminService.editVerifiedInfoResult$
      .pipe(
        withLatestFrom(
          this.vm$,
          this.store.select(UserServicePersonaSelectors.selectUserServicePersonaEntities)
        ),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(([updatedVerifiedInfo, vm, userServicePersonaEntities]) => {
        const personaId = vm.personaId?.toString()
        const patchedVerifiedInfo = {
          ...vm.verifiedInfo,
          ...updatedVerifiedInfo,
          persona_id: updatedVerifiedInfo.persona_id ?? vm.verifiedInfo?.persona_id ?? personaId,
          org_id: updatedVerifiedInfo.org_id ?? vm.verifiedInfo?.org_id ?? vm.orgId,
          root_org_id: updatedVerifiedInfo.root_org_id ?? vm.verifiedInfo?.root_org_id,
          approved_at: updatedVerifiedInfo.approved_at ?? vm.verifiedInfo?.approved_at,
          verified: updatedVerifiedInfo.verified ?? vm.verifiedInfo?.verified,
        } as VerifiedInformation
        const resultPersonaId = patchedVerifiedInfo.persona_id?.toString()
        if (!personaId || personaId !== resultPersonaId) return

        const existingPersona = userServicePersonaEntities[personaId]
        if (existingPersona) {
          const updatedPersona = this.withEditedVerifiedInfo(
            existingPersona,
            patchedVerifiedInfo,
            vm.verifiedInfo
          )
          this.store.dispatch(UserServicePersonaApiActions.setUserServicePersonaEntity({
            resource: updatedPersona,
          }))
        }

        this.sidePanelChange.emit({ type: 'editVerifiedInfoChange', data: patchedVerifiedInfo })
      })
  }

  private withEditedVerifiedInfo(
    existingPersona: Persona,
    updatedVerifiedInfo: VerifiedInformation,
    previousVerifiedInfo?: VerifiedInformation | null
  ): Persona {
    const verifiedInformation = existingPersona.verified_information || []
    const updateIndex = verifiedInformation.findIndex(verifiedInfo =>
      verifiedInfo === previousVerifiedInfo ||
      this.matchesVerifiedInfo(verifiedInfo, previousVerifiedInfo) ||
      this.matchesVerifiedInfo(verifiedInfo, updatedVerifiedInfo)
    )

    const nextVerifiedInformation = [...verifiedInformation]
    if (updateIndex >= 0) {
      nextVerifiedInformation[updateIndex] = {
        ...nextVerifiedInformation[updateIndex],
        ...updatedVerifiedInfo,
      }
    } else {
      nextVerifiedInformation.push(updatedVerifiedInfo)
    }

    return {
      ...existingPersona,
      verified_information: nextVerifiedInformation,
    }
  }

  private matchesVerifiedInfo(
    candidate: VerifiedInformation,
    verifiedInfo?: VerifiedInformation | null
  ): boolean {
    if (!verifiedInfo) return false

    return candidate.persona_id?.toString() === verifiedInfo.persona_id?.toString() &&
      candidate.org_id?.toString() === verifiedInfo.org_id?.toString() &&
      candidate.root_org_id?.toString() === verifiedInfo.root_org_id?.toString() &&
      candidate.approved_at === verifiedInfo.approved_at
  }

  private withEditedPersonaFields(
    existingPersona: UserPersona,
    updatedPersona: cs.Profiles.Persona & { preferred_name?: string }
  ): UserPersona {
    const firstName = updatedPersona.first_name
    const lastName = updatedPersona.last_name
    const suffix = updatedPersona.suffix
    const displayName = [firstName, lastName, suffix].filter(Boolean).join(' ')

    return {
      ...existingPersona,
      first_name: firstName,
      last_name: lastName,
      nickname: updatedPersona.preferred_name ?? updatedPersona.nickname,
      suffix,
      gender: updatedPersona.gender,
      date_of_birth: updatedPersona.date_of_birth,
      graduation_year: updatedPersona.graduation_year,
      full_name: updatedPersona.full_name || displayName,
      name: updatedPersona.name || updatedPersona.full_name || displayName,
    } as unknown as UserPersona
  }

  private clearPersonaRequests(): void {
    this.lastVerifiedInfoLoadKey = undefined
    this.store.dispatch(NginUserPersonaApiActions.clearNginUserPersonaRequests({
      requestKeyPrefix: PROFILE_SIDE_PANEL_REQUEST_PREFIX,
    }))
    this.store.dispatch(NginAdminManagementApiActions.clearNginAdminManagementRequests({
      requestKeyPrefix: PROFILE_SIDE_PANEL_REQUEST_PREFIX,
    }))
    this.store.dispatch(UserServicePersonaApiActions.clearUserServicePersonaRequests({
      requestKeyPrefix: PROFILE_SIDE_PANEL_REQUEST_PREFIX,
    }))
    this.store.dispatch(NginSurveyResultApiActions.clearNginSurveyResultRequests({
      requestKeyPrefix: PROFILE_SIDE_PANEL_REQUEST_PREFIX,
    }))
    this.store.dispatch(NginGroupApiActions.clearNginGroupRequests({
      requestKeyPrefix: PROFILE_SIDE_PANEL_REQUEST_PREFIX,
    }))
  }

  private clearOrganizationRequests(): void {
    this.store.dispatch(BossServiceOrganizationApiActions.clearBossServiceOrganizationRequests({
      requestKeyPrefix: PROFILE_SIDE_PANEL_REQUEST_PREFIX,
    }))
  }

  private clearRequests(): void {
    this.clearPersonaRequests()
    this.clearOrganizationRequests()
  }
}
